import {
  Menu,
  MenuButton,
  MenuCheckedValueChangeData,
  MenuCheckedValueChangeEvent,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  menuItemCheckboxClassNames,
  shorthands,
  tokens,
  typographyStyles,
} from '@fluentui/react-components';
import { Checkmark12Filled } from '@fluentui/react-icons';
import { useMemo } from 'react';

import { AI_ASSISTANT_TITLE as AI_LICENSE_LABEL } from '@modules/ai/constants';

import { TLicense, TUpdateUser, TUser } from '../types/users.types';

const BASE_LICENSE_GROUP = 'base-license';
const ADDITIONAL_LICENSE_GROUP = 'additional-license';
const AI_LICENSE = 'AI';

const LICENSE_TYPES: Record<TLicense, string> = {
  NONE: 'None',
  READ: 'Read only',
  FULL: 'Full',
};

const useStyles = makeStyles({
  trigger: {
    ...typographyStyles.body1,
    borderBottomColor: tokens.colorNeutralStrokeAccessible,
    justifyContent: 'space-between',
    minWidth: 0,
    width: '100%',
    ':hover': {
      borderBottomColor: tokens.colorNeutralStrokeAccessible,
    },
    ':hover:active': {
      borderBottomColor: tokens.colorNeutralStrokeAccessible,
    },
    '&[data-fui-focus-visible], &[aria-expanded="true"]': {
      ...shorthands.borderColor(tokens.colorTransparentStroke),
      borderBottomColor: tokens.colorCompoundBrandStroke,
      boxShadow: `inset 0 -${tokens.strokeWidthThin} ${tokens.colorCompoundBrandStroke}`,
    },
  },
  menuIcon: {
    color: tokens.colorNeutralStrokeAccessible,
    fontSize: '20px',
    height: '20px',
    width: '20px',
  },
  additionalLicenseItem: {
    [`& .${menuItemCheckboxClassNames.checkmark}`]: {
      alignItems: 'center',
      border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStrokeAccessible}`,
      borderRadius: tokens.borderRadiusSmall,
      boxSizing: 'border-box',
      display: 'flex',
      fill: 'currentColor',
      flexShrink: 0,
      fontSize: '12px',
      height: '16px',
      justifyContent: 'center',
      visibility: 'visible',
      width: '16px',
      '& svg': {
        visibility: 'hidden',
      },
    },
    [`&[aria-checked="true"] .${menuItemCheckboxClassNames.checkmark}`]: {
      backgroundColor: tokens.colorCompoundBrandBackground,
      color: tokens.colorNeutralForegroundInverted,
      ...shorthands.borderColor(tokens.colorCompoundBrandBackground),
      '& svg': {
        visibility: 'visible',
      },
    },
    [`&[aria-disabled="true"] .${menuItemCheckboxClassNames.checkmark}`]: {
      color: tokens.colorNeutralForegroundDisabled,
      ...shorthands.borderColor(tokens.colorNeutralForegroundDisabled),
    },
  },
});

type TProps = {
  user: TUser;
  isAiLicenseLimitReached: boolean;
  isUpdating: boolean;
  onChange: (update: TUpdateUser) => void;
};

const UserLicensesMenu = ({ user, isAiLicenseLimitReached, isUpdating, onChange }: TProps) => {
  const checkedValues = useMemo(
    () => ({
      [BASE_LICENSE_GROUP]: [user.license],
      [ADDITIONAL_LICENSE_GROUP]: user.hasAiLicense ? [AI_LICENSE] : [],
    }),
    [user.hasAiLicense, user.license]
  );
  const displayValue = `${LICENSE_TYPES[user.license]}${user.hasAiLicense ? `, ${AI_LICENSE_LABEL}` : ''}`;
  const isAiLicenseDisabled = !user.hasAiLicense && (user.license === 'NONE' || isAiLicenseLimitReached);

  const onCheckedValueChange = (_event: MenuCheckedValueChangeEvent, data: MenuCheckedValueChangeData) => {
    if (isUpdating) return;

    if (data.name === BASE_LICENSE_GROUP) {
      const license = data.checkedItems[0] as TLicense | undefined;
      if (license && license !== user.license) onChange({ license });
      return;
    }

    if (data.name === ADDITIONAL_LICENSE_GROUP) {
      const hasAiLicense = data.checkedItems.includes(AI_LICENSE);
      if (hasAiLicense !== user.hasAiLicense) onChange({ hasAiLicense });
    }
  };

  const classes = useStyles();

  return (
    <Menu checkedValues={checkedValues} onCheckedValueChange={onCheckedValueChange}>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton
          appearance="transparent"
          shape="square"
          className={classes.trigger}
          menuIcon={{ className: classes.menuIcon }}
        >
          {displayValue}
        </MenuButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuGroup>
            <MenuGroupHeader>Standard licenses</MenuGroupHeader>
            {Object.entries(LICENSE_TYPES).map(([license, label]) => (
              <MenuItemRadio key={license} name={BASE_LICENSE_GROUP} value={license}>
                {label}
              </MenuItemRadio>
            ))}
          </MenuGroup>
          <MenuDivider />
          <MenuGroup>
            <MenuGroupHeader>Additional licenses</MenuGroupHeader>
            <MenuItemCheckbox
              className={classes.additionalLicenseItem}
              checkmark={{ children: <Checkmark12Filled /> }}
              name={ADDITIONAL_LICENSE_GROUP}
              value={AI_LICENSE}
              disabled={isAiLicenseDisabled}
              persistOnClick
            >
              {AI_LICENSE_LABEL}
            </MenuItemCheckbox>
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default UserLicensesMenu;
